/**
 * components/clients/FieldConflictRow.tsx
 *
 * Renders a single row in the merge conflict resolution step.
 * Shows the field label, two radio buttons (target vs source values),
 * and requires an explicit choice - no pre-selection.
 * Created Apr 2026.
 * Fix Apr 2026: wrapped radio group in fieldset/legend for accessibility;
 *   refactored side rendering into a declarative options array.
 */

import type { FieldConflict, FieldResolutionChoice, FieldResolutions } from '../../types/merge';

interface FieldConflictRowProps {
  conflict: FieldConflict;
  choice: FieldResolutionChoice | undefined;
  onChoose: (field: keyof FieldResolutions, choice: FieldResolutionChoice) => void;
}

const OPTIONS: { key: FieldResolutionChoice; label: string }[] = [
  { key: 'target', label: 'Fiche à conserver' },
  { key: 'source', label: 'Fiche à fusionner' },
];

export default function FieldConflictRow({ conflict, choice, onChoose }: FieldConflictRowProps) {
  return (
    <fieldset className="grid grid-cols-1 sm:grid-cols-[120px_1fr_1fr] gap-2 items-start py-3 border-b border-neutral-100 last:border-0 min-w-0">
      <legend className="float-left w-[120px] hidden sm:flex items-center pt-1">
        <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
          {conflict.label}
        </span>
      </legend>
      {/* Mobile-only label visible above the options */}
      <p className="sm:hidden text-xs font-semibold text-neutral-500 uppercase tracking-wide col-span-full">
        {conflict.label}
      </p>

      {OPTIONS.map((option) => {
        const value = option.key === 'target' ? conflict.targetValue : conflict.sourceValue;
        const isSelected = choice === option.key;
        return (
          <label
            key={option.key}
            className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
              isSelected
                ? 'border-primary-400 bg-primary-50'
                : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
            }`}
          >
            <input
              type="radio"
              name={`conflict-${conflict.field}`}
              value={option.key}
              checked={isSelected}
              onChange={() => onChoose(conflict.field, option.key)}
              className="mt-0.5 accent-primary-600 flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="text-xs font-medium text-neutral-400 mb-0.5">
                {option.label}
              </p>
              <p className="text-sm text-neutral-900 break-words leading-snug">
                {value || <span className="text-neutral-400 italic">Vide</span>}
              </p>
            </div>
          </label>
        );
      })}
    </fieldset>
  );
}
